import { Info } from "lucide-react";
import { Box, Divider, Typography } from "@mui/material";
import { Button, palette, Switch } from "@powerdiary/components-library/lib";
import { EmailCategory } from "types/generated/models";
import { EMAIL_CATEGORY_OPTIONS, EmailPreferenceValues } from "../EmailPreferences.helpers";
import styles from "./EmailPreferencesForm.module.css";

const AFFECTED_EMAILS = [
    "Appointment reminders",
    "Invoices, statements and receipts",
    "Forms and clinical notes",
    "Practice updates",
];

export interface IEmailPreferencesFormProps {
    email: string;
    businessName: string;
    values: EmailPreferenceValues;
    isSaving: boolean;
    onChange: (category: EmailCategory, receives: boolean) => void;
    onSubmit: () => void;
}

export default function EmailPreferencesForm({
    email,
    businessName,
    values,
    isSaving,
    onChange,
    onSubmit,
}: IEmailPreferencesFormProps) {
    return (
        <Box className={styles.form} data-test-id="section_email_preferences_form">
            {/* Not InfoBox: its content always gets a top margin, so the padding is uneven without a title. */}
            <Box
                className={styles.warning}
                sx={{ backgroundColor: palette.dusk["100"], borderColor: palette.dusk["500"] }}
                data-test-id="info_box_email_preferences_warning"
            >
                <Info className={styles.warningIcon} size={20} aria-hidden />
                <Typography variant="paragraphMediumRegular" className={styles.warningText}>
                    You are about to unsubscribe <strong>{email}</strong> from all email communication sent by{" "}
                    {businessName}.
                </Typography>
            </Box>

            <Box data-test-id="section_email_preferences_affected">
                <Typography variant="paragraphMediumRegular">
                    This is not limited to marketing. If you continue, you will stop receiving <strong>every</strong>{" "}
                    email from this practice, including:
                </Typography>
                <ul className={styles.affectedList}>
                    {AFFECTED_EMAILS.map((title) => (
                        <li key={title}>
                            <Typography variant="paragraphMediumRegular">
                                <strong>{title}</strong>
                            </Typography>
                        </li>
                    ))}
                </ul>
            </Box>

            <Typography variant="paragraphMediumRegular">
                Prefer to keep some of it? Choose what you would still like to receive.
            </Typography>

            <Box className={styles.toggles} data-test-id="group_email_preferences_toggles">
                {EMAIL_CATEGORY_OPTIONS.map(({ category, label, description, testIdSuffix }) => (
                    <Box key={category} className={styles.toggleRow}>
                        <Box className={styles.toggleText}>
                            <Typography variant="paragraphMediumRegular" id={`email_preference_${testIdSuffix}`}>
                                {label}
                            </Typography>
                            <Typography variant="paragraphExtraSmallRegular" className={styles.description}>
                                {description}
                            </Typography>
                        </Box>
                        <Switch
                            className={styles.toggleSwitch}
                            dataTestIdSuffix={`email_preference_${testIdSuffix}`}
                            checked={values[category]}
                            onChange={(_, checked) => onChange(category, checked)}
                            slotProps={{ input: { "aria-labelledby": `email_preference_${testIdSuffix}` } }}
                        />
                    </Box>
                ))}
            </Box>

            <Divider />

            <Box className={styles.actions}>
                {/* The only way to change anything: link scanners load this page but never press buttons. */}
                <Button
                    variant="contained"
                    onClick={onSubmit}
                    disabled={isSaving}
                    data-test-id="button_update_email_preferences"
                >
                    Save preferences
                </Button>
            </Box>
        </Box>
    );
}
